import { Star } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const HERO_ID = "1696418800218-6f3bb0975d3b";
const heroUrl = (w) =>
  `https://images.unsplash.com/photo-${HERO_ID}?crop=entropy&cs=srgb&fm=jpg&q=65&w=${w}`;
const HERO_SRCSET = [640, 960, 1280, 1600]
  .map((w) => `${heroUrl(w)} ${w}w`)
  .join(", ");

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_0455e07c-95ba-4da1-86cd-b6b640dcd3e8/artifacts/c1n4bm9e_Haceinda%20Carey%20Cover.png";

export default function Hero() {
  const { t } = useLanguage();
  const h = t.hero;

  return (
    <section
      id="top"
      className="relative isolate min-h-[100svh] w-full overflow-hidden bg-[#0A0A0A] text-white"
      data-testid="hero-section"
    >
      {/* Background — responsive img so mobile fetches a small file (LCP) */}
      <img
        src={heroUrl(1280)}
        srcSet={HERO_SRCSET}
        sizes="100vw"
        alt=""
        fetchPriority="high"
        decoding="async"
        className="absolute inset-0 -z-10 h-full w-full object-cover object-center"
        aria-hidden="true"
      />
      {/* Overlays */}
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-black/55 via-black/35 to-black/85" />
      <div className="pointer-events-none absolute -top-24 -right-32 -z-10 h-[520px] w-[520px] rounded-full bg-[#FF5533]/30 blur-[140px]" />
      <div className="pointer-events-none absolute -bottom-32 -left-32 -z-10 h-[420px] w-[420px] rounded-full bg-[#FFB800]/15 blur-[140px]" />

      {/* Content */}
      <div className="relative mx-auto flex min-h-[100svh] max-w-7xl flex-col justify-end px-6 pb-16 pt-32 md:px-10 md:pb-24 md:pt-40">
        <div className="max-w-3xl">
          {/* Brand medallion — client-provided Hacienda Carey logo */}
          <img
            src={LOGO_URL}
            alt="Hacienda Carey"
            width={112}
            height={112}
            decoding="async"
            className="mb-8 h-24 w-24 rounded-full object-cover ring-1 ring-white/20 shadow-2xl shadow-black/50 md:h-28 md:w-28"
            data-testid="hero-logo-medallion"
          />

          <p
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/5 px-4 py-1.5 text-[11px] uppercase tracking-[0.28em] text-white/80 backdrop-blur-md"
            data-testid="hero-eyebrow"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-[#FFB800]" />
            {h.eyebrow}
          </p>

          <h1
            className="font-serif text-5xl leading-[0.98] tracking-tight md:text-7xl lg:text-[5.5rem]"
            style={{ fontFamily: "Fraunces, serif" }}
            data-testid="hero-title"
          >
            <span className="block font-normal">{h.title_pre}</span>
            <span className="mt-1 block italic text-[#FF5533]">
              {h.title_em}
            </span>
            <span className="mt-1 block font-normal">{h.title_post}</span>
          </h1>

          <p
            className="mt-6 max-w-2xl text-base text-white/80 md:text-lg"
            data-testid="hero-subtitle"
          >
            {h.sub}
          </p>

          {/* CTAs */}
          <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center">
            <a
              href="#activities"
              data-testid="hero-cta-primary"
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#FF5533] px-7 py-4 text-sm font-semibold uppercase tracking-wider text-white shadow-xl shadow-[#FF5533]/30 transition-all hover:-translate-y-0.5 hover:bg-[#E64A2B]"
            >
              {h.cta_primary}
              <span className="transition-transform duration-300 group-hover:translate-x-1">
                →
              </span>
            </a>
            <a
              href="#about"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-white/30 bg-white/5 px-7 py-4 text-sm font-semibold uppercase tracking-wider text-white backdrop-blur-md transition-all hover:-translate-y-0.5 hover:border-white/50 hover:bg-white/10"
            >
              {h.cta_secondary}
            </a>
          </div>

          {/* Stats strip */}
          <div className="mt-12 grid max-w-2xl grid-cols-3 gap-6 border-t border-white/15 pt-8">
            <div data-testid="hero-stat-rating">
              <div className="flex items-center gap-1.5">
                <Star size={16} className="fill-[#FFB800] text-[#FFB800]" />
                <span className="font-serif text-3xl leading-none md:text-4xl"
                  style={{ fontFamily: "Fraunces, serif" }}>
                  4.7
                </span>
              </div>
              <p className="mt-2 text-[11px] uppercase tracking-wider text-white/60">
                {h.stat_rating}
              </p>
            </div>
            <div data-testid="hero-stat-reviews">
              <span
                className="font-serif text-3xl leading-none md:text-4xl"
                style={{ fontFamily: "Fraunces, serif" }}
              >
                97+
              </span>
              <p className="mt-2 text-[11px] uppercase tracking-wider text-white/60">
                {h.stat_reviews}
              </p>
            </div>
            <div data-testid="hero-stat-years">
              <span
                className="font-serif text-3xl leading-none md:text-4xl"
                style={{ fontFamily: "Fraunces, serif" }}
              >
                2019
              </span>
              <p className="mt-2 text-[11px] uppercase tracking-wider text-white/60">
                {h.stat_years}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Decorative scroll cue */}
      <div
        className="pointer-events-none absolute bottom-6 right-6 hidden flex-col items-end gap-2 text-[10px] uppercase tracking-[0.3em] text-white/60 md:flex"
        aria-hidden="true"
      >
        <span>Scroll</span>
        <span className="block h-10 w-px bg-gradient-to-b from-white/60 to-transparent" />
      </div>
    </section>
  );
}
